import Phaser from 'phaser';
import { FACTION_COLORS } from '../../shared/factions';
import type { FactionId } from '../../shared/types';

/**
 * Victory confetti burst — 60 small rectangles fired in a radial pattern,
 * gravity-pulled, fading over 2s. Colored by the 5 faction primaries so the
 * burst doubles as a "party" + faction identity reinforce.
 *
 * Pure helper: pass any Phaser scene + center point + duration override.
 */
export const CONFETTI_PARTICLE_COUNT = 60;
export const CONFETTI_DURATION_MS = 2000;
const CONFETTI_FACTIONS: FactionId[] = ['celestial', 'runic', 'techno', 'elemental', 'dominion'];

export interface ConfettiOptions {
  /** Override particle count (default 60). 80 = max-feel for boss-victory. */
  count?: number;
  /** Override duration ms (default 2000). */
  durationMs?: number;
  /** Initial radial speed range — particles get a random vx/vy in this range. */
  speedMin?: number;
  speedMax?: number;
}

/**
 * Spawn confetti. Returns the number of particles actually created (for tests
 * and harness-safe failure modes).
 */
export function spawnConfetti(
  scene: Phaser.Scene,
  centerX: number,
  centerY: number,
  opts: ConfettiOptions = {},
): number {
  const count = opts.count ?? CONFETTI_PARTICLE_COUNT;
  const duration = opts.durationMs ?? CONFETTI_DURATION_MS;
  const speedMin = opts.speedMin ?? 120;
  const speedMax = opts.speedMax ?? 260;
  let spawned = 0;

  for (let i = 0; i < count; i++) {
    const faction = CONFETTI_FACTIONS[i % CONFETTI_FACTIONS.length];
    const color = FACTION_COLORS[faction].primary;
    const angle = Math.random() * Math.PI * 2;
    const speed = speedMin + Math.random() * (speedMax - speedMin);
    const targetX = centerX + Math.cos(angle) * speed * (duration / 1000);
    // Gravity pull — add downward drift on top of initial radial velocity.
    const gravityFall = 220 * (duration / 1000);
    const targetY = centerY + Math.sin(angle) * speed * (duration / 1000) + gravityFall;

    let p: Phaser.GameObjects.Rectangle;
    try {
      p = scene.add.rectangle(centerX, centerY, 6, 4, color, 1);
      if (typeof (p as { setDepth?: unknown }).setDepth === 'function') p.setDepth(20);
      if (typeof (p as { setRotation?: unknown }).setRotation === 'function') {
        p.setRotation(Math.random() * Math.PI * 2);
      }
    } catch {
      continue;
    }

    try {
      scene.tweens.add({
        targets: p,
        x: targetX,
        y: targetY,
        rotation: Math.random() * Math.PI * 4,
        alpha: 0,
        duration,
        ease: 'Cubic.easeIn',
        onComplete: () => { try { p.destroy(); } catch { /* noop */ } },
      });
      spawned++;
    } catch {
      try { p.destroy(); } catch { /* noop */ }
    }
  }
  return spawned;
}
